import { useCallback, useMemo, useRef, useState } from 'react';
import { type Api, ApiHttpError } from '../api';

/** 확인 경로가 없는 옛 서버의 응답 코드. */
const HTTP_NOT_FOUND = 404;
import {
  type EmailCheckState,
  type EmailCheckView,
  emailCheckView,
  isEmailFormat,
  normalizeEmail,
} from '../emailRules';

/** `useEmailCheck` 가 돌려주는 것 — 화면 부품(`EmailCheckField`)과 저장 버튼이 함께 쓴다. */
export interface EmailCheck {
  /** 지금 입력값 기준 표시(문구·색·확인됨 여부·버튼 가능 여부). */
  view: EmailCheckView;
  /** [중복 확인] — 형식이 틀리면 서버에 묻지 않고 형식 문구를 띄운다. */
  check: () => Promise<void>;
  /** 칸을 떠났다(onBlur) — 이때부터 형식 오류를 보인다. */
  touch: () => void;
  /** 폼을 비울 때(추가 완료·취소) 결과도 함께 지운다. */
  reset: () => void;
}

/**
 * 이메일 [중복 확인] 상태 훅(2026-09-29). 규칙은 `emailRules.ts`(순수), 여기는 서버 호출과 상태만.
 *
 * - 결과는 **확인한 주소**에 묶인다 → 확인 뒤 입력을 고치면 `view.confirmed` 가 자동으로 false(다시 확인해야 저장 가능).
 * - 응답이 늦게 와서 그 사이 다른 주소를 확인했으면 옛 응답은 버린다(마지막 요청만 반영).
 *
 * @param api   서버 API(로그인한 사용자 토큰)
 * @param value 입력칸의 지금 값(부모가 들고 있다 — 폼 상태를 한 곳에 두려고)
 */
export function useEmailCheck(api: Api, value: string): EmailCheck {
  const [state, setState] = useState<EmailCheckState>({ kind: 'idle' });
  const [touched, setTouched] = useState(false);
  // 마지막으로 물어본 주소 — 늦게 온 옛 응답을 거르는 기준.
  const latest = useRef('');

  const check = useCallback(async () => {
    setTouched(true);
    if (!isEmailFormat(value)) return; // 형식 문구는 view 가 띄운다
    const email = normalizeEmail(value);
    latest.current = email;
    setState({ kind: 'checking', email });
    try {
      const r = await api.checkEmail(email);
      if (latest.current !== email) return;
      setState(r.available ? { kind: 'available', email } : { kind: 'taken', email, mine: r.mine });
    } catch (e) {
      if (latest.current !== email) return;
      // 옛 서버(≤ c64dfa3)는 이 경로가 없다 → 막지 말고 저장 때 409 로 판정(서버 배포 전 앱·시험 빌드에서도 사용자 추가가 되게).
      if (e instanceof ApiHttpError && e.status === HTTP_NOT_FOUND) {
        setState({ kind: 'unsupported', email });
        return;
      }
      setState({ kind: 'error', email, message: (e as Error).message || '확인하지 못했습니다. 다시 눌러 주세요.' });
    }
  }, [api, value]);

  const touch = useCallback(() => setTouched(true), []);
  const reset = useCallback(() => {
    latest.current = '';
    setState({ kind: 'idle' });
    setTouched(false);
  }, []);

  const view = useMemo(() => emailCheckView(value, state, touched), [value, state, touched]);
  return { view, check, touch, reset };
}
